/**
 * DECISIONS 200 step 3 (B1, the owner's pick: "Maybe commander can determine as there are times when you'd want to spawn not
 * at the furthest forward spawn"): a side's standing order on where its waves land — a `spawnAt` input from the table, like
 * 68.25's vehicles order, kept for the match:
 * - `forward` (absent; the default and the bot's): the one ranking's first open point (core/drops.ts rankedSpawn), with the
 *   hold-back — a point the side's picture puts a known enemy near in the last few seconds (contested), or where one of its
 *   arrivals was lost soon after landing (hot), is passed over for the next (SPAWN_HOLD);
 * - `base`: where it lands anyway (its base; under Operations the push's landing);
 * - a named spawn point of its side (a beacon, relay or FOB): there while it can take the side's arrivals, else forward.
 * A human seat's own pick (69.6h) still comes first for the units it holds. Pure; with nobody ordering and no arrival at a
 * spawn point, nothing here writes a field.
 */
import type { GameState, StructureState, Vec3 } from '../types';
import { SPAWN_HOLD, PHYS_DT } from '../constants';
import { PERSON_BODY_HEIGHT, personSetOf } from './scaleProfile';
import { structureJob, structureTeam } from './body';
import { spawnSiteRefusal } from './build';
import { isLinked } from './web';
import { canDropAt, rankedSpawn } from './drops';
import { teamPicture } from './picture';
import { arcDistance } from './gameState';
import { battlePlanetIndex } from './actionSetup';

export type SpawnAt = 'forward' | 'base' | { structureId: number };

/** The side's order (forward when it has none). */
export function spawnAtOf(state: Pick<GameState, 'spawnAt'>, team: number): SpawnAt {
  return state.spawnAt?.[team] ?? 'forward';
}

/** Why `at` can't be `team`'s order (null: it can): a named point must be one of its spawn points. One lost later is not refused: the order waits (forward meanwhile). */
export function spawnAtRefusal(state: GameState, team: number, at: SpawnAt): string | null {
  if (at === 'forward' || at === 'base') return null;
  const s = state.structures.find(x => x.id === at.structureId);
  if (!s || (s.type !== 'beacon' && s.type !== 'relay' && s.type !== 'fob')) return 'Pick a beacon, relay or FOB';
  return structureTeam(state, s) === team ? null : 'Not your side’s';
}

/** The state with `team`'s order set (`forward` drops the entry; the record absent once no side orders). */
export function applySpawnAt(state: GameState, team: number, at: SpawnAt): GameState {
  const { [team]: _was, ...rest } = state.spawnAt ?? {};
  const next = at === 'forward' ? rest : { ...rest, [team]: at };
  const { spawnAt: _old, ...base } = state;
  return Object.keys(next).length ? { ...base, spawnAt: next } : base as GameState;
}

/** Can `s` take `team`'s arrivals now: a standing beacon (the beacons switch on) where a spawn may stand; a relay that can take a drop; under Operations a linked FOB where a spawn may stand. */
export function spawnPointOpen(state: GameState, team: number, s: StructureState, ops: boolean): boolean {
  if (s.health <= 0 || structureTeam(state, s) !== team) return false;
  if (s.type === 'beacon') return !!state.config.beacons && structureJob(s).spawn === true && spawnSiteRefusal(state, team, s) === null;
  if (s.type === 'relay') return canDropAt(state, s);
  if (s.type === 'fob') return ops && isLinked(state, s) && spawnSiteRefusal(state, team, s) === null;
  return false;
}

/**
 * The hold-back's closed points for `team` at `tick`: its spawn points (beacons, relays, FOBs) its picture puts a known enemy
 * within SPAWN_HOLD.contestPeople of, from the last SPAWN_HOLD.contestSeconds (contested), and those hot (`hotSpawns`).
 */
export function closedSpawns(state: GameState, team: number, tick: number): Set<number> {
  const out = new Set<number>();
  for (const [id, until] of Object.entries(state.hotSpawns ?? {})) if (until > tick) out.add(Number(id));
  const bi = battlePlanetIndex(state), planet = state.planets[bi];
  const mine = state.structures.filter(s => (s.type === 'beacon' || s.type === 'relay' || s.type === 'fob') && s.health > 0 && s.planetIndex === bi && structureTeam(state, s) === team);
  if (!planet || !mine.length) return out;
  const near = SPAWN_HOLD.contestPeople * PERSON_BODY_HEIGHT * personSetOf(state).k, fresh = Math.round(SPAWN_HOLD.contestSeconds / PHYS_DT);
  const seen = teamPicture(state, team, tick).filter(b => b.planetIndex === bi && tick - b.tick <= fresh);
  for (const s of mine) if (seen.some(b => arcDistance(b.surfaceDir, s.surfaceDir, planet.radius) <= near)) out.add(s.id);
  return out;
}

/**
 * Where `team`'s arrival lands by its order: `base` — null (its fallback); a named point while open; else forward, the ranking
 * with the hold-back's `closed` points left out (rankedSpawn's other arguments are its).
 */
export function sideSpawn(state: GameState, team: number, toward: Vec3 | null, fallback: Vec3 | null, beacon: StructureState | null, ops: boolean, closed: ReadonlySet<number>): StructureState | null {
  const at = spawnAtOf(state, team);
  if (at === 'base') return null;
  if (at !== 'forward') {
    const s = state.structures.find(x => x.id === at.structureId);
    if (s && spawnPointOpen(state, team, s, ops)) return s;
  }
  return rankedSpawn(state, team, toward, fallback, beacon, ops, closed);
}

/** The arrivals at spawn points this wave, logged to be watched for SPAWN_HOLD.hotSeconds (absent: none to log). */
export function noteSpawnArrivals(state: GameState, arrivals: ReadonlyArray<{ unitId: number; at: number }>, tick: number): GameState {
  if (!arrivals.length) return state;
  return { ...state, spawnLog: [...(state.spawnLog ?? []).filter(e => !arrivals.some(a => a.unitId === e.unitId)), ...arrivals.map(a => ({ ...a, tick }))] };
}

/**
 * At a wave: an arrival watched that is down again within SPAWN_HOLD.hotSeconds of landing (checked at the waves, so within
 * that and a wave's beat) makes its point hot for SPAWN_HOLD.hotSeconds; the watch ends then either way. Expired heat is dropped.
 */
export function stepHotSpawns(state: GameState, tick: number, waveTicks: number): GameState {
  if (!state.spawnLog?.length && !state.hotSpawns) return state;
  const hot = Object.fromEntries(Object.entries(state.hotSpawns ?? {}).filter(([, until]) => until > tick).map(([k, v]) => [k, v])) as Record<number, number>;
  const window = Math.round(SPAWN_HOLD.hotSeconds / PHYS_DT), keep: NonNullable<GameState['spawnLog']> = [];
  for (const e of state.spawnLog ?? []) {
    const u = state.players.find(p => p.id === e.unitId);
    if (u && !u.alive && tick - e.tick <= window + waveTicks) hot[e.at] = Math.max(hot[e.at] ?? 0, tick + window);
    else if (tick - e.tick <= window) keep.push(e);
  }
  const { spawnLog: _l, hotSpawns: _h, ...base } = state;
  return { ...base, ...(keep.length ? { spawnLog: keep } : {}), ...(Object.keys(hot).length ? { hotSpawns: hot } : {}) } as GameState;
}
